import React from "react";
import { render, screen, fireEvent } from "@testing-library/react";
import "@testing-library/jest-dom";
import AutodbTooltipWrapper, {
  AutodbSourceButton,
  AutodbTooltipHost,
} from "./AutodbTooltipWrapper";

// jsdom has no ResizeObserver; react-tooltip constructs one when it shows.
beforeAll(() => {
  if (!(global as any).ResizeObserver) {
    (global as any).ResizeObserver = class {
      observe() {}
      unobserve() {}
      disconnect() {}
    };
  }
});

const ALPHA = {
  source_id: "https://alpha.example/report",
  data_comment: "Alpha comment",
};
const BETA = {
  source_id: "https://beta.example/report",
  data_comment: "Beta comment",
};

describe("AutodbTooltipWrapper with a shared host", () => {
  it("shows the hovered anchor's own content, not another anchor's", async () => {
    render(
      <>
        <AutodbTooltipWrapper info={ALPHA}>alpha cell</AutodbTooltipWrapper>
        <AutodbTooltipWrapper info={BETA}>beta cell</AutodbTooltipWrapper>
        <AutodbTooltipHost />
      </>,
    );

    const [alphaIcon, betaIcon] = screen.getAllByRole("button", {
      name: "Source details",
    });
    fireEvent.mouseEnter(alphaIcon);
    expect(await screen.findByText("Alpha comment")).toBeInTheDocument();
    expect(screen.queryByText("Beta comment")).not.toBeInTheDocument();

    fireEvent.mouseLeave(alphaIcon);
    fireEvent.mouseEnter(betaIcon);
    expect(await screen.findByText("Beta comment")).toBeInTheDocument();
  });

  it("renders the source as a link when it is a URL", async () => {
    render(
      <>
        <AutodbTooltipWrapper info={ALPHA}>alpha cell</AutodbTooltipWrapper>
        <AutodbTooltipHost />
      </>,
    );

    fireEvent.mouseEnter(
      screen.getByRole("button", { name: "Source details" }),
    );
    const link = await screen.findByRole("link", {
      name: "https://alpha.example/report",
    });
    expect(link).toHaveAttribute("href", "https://alpha.example/report");
  });

  it("leaves the value itself alone: the icon carries the tooltip", async () => {
    render(
      <>
        <AutodbTooltipWrapper info={ALPHA}>alpha cell</AutodbTooltipWrapper>
        <AutodbTooltipHost />
      </>,
    );

    const value = screen.getByText("alpha cell");
    expect(value).not.toHaveAttribute("data-tooltip-id");
    fireEvent.mouseEnter(value);
    await new Promise((resolve) => setTimeout(resolve, 100));
    expect(screen.queryByText("Alpha comment")).not.toBeInTheDocument();
  });

  it("shows no icon for an empty value", () => {
    render(
      <>
        <AutodbTooltipWrapper info={ALPHA}>{""}</AutodbTooltipWrapper>
        <AutodbTooltipWrapper info={ALPHA}>{null}</AutodbTooltipWrapper>
        <AutodbTooltipWrapper info={ALPHA}>
          <p>{""}</p>
        </AutodbTooltipWrapper>
        <AutodbTooltipWrapper info={ALPHA}>
          <div>
            <p>{undefined}</p>
          </div>
        </AutodbTooltipWrapper>
        <AutodbTooltipHost />
      </>,
    );

    expect(screen.queryByRole("button")).not.toBeInTheDocument();
  });

  it("counts a component given its value as props as a value", () => {
    const Detail = ({ value }: { value: string }) => <p>{value}</p>;
    render(
      <>
        <AutodbTooltipWrapper info={ALPHA}>
          <Detail value="Oncology" />
        </AutodbTooltipWrapper>
        <AutodbTooltipHost />
      </>,
    );

    expect(
      screen.getByRole("button", { name: "Source details" }),
    ).toBeInTheDocument();
  });

  it("renders children untouched and anchors nothing when info is absent", () => {
    render(
      <>
        <AutodbTooltipWrapper>plain cell</AutodbTooltipWrapper>
        <AutodbTooltipHost />
      </>,
    );

    // With info, the text sits inside a <span data-tooltip-id>, so getByText
    // would return that span. Without it the children render bare, and the
    // element holding the text must carry no anchor attribute.
    const cell = screen.getByText("plain cell");
    expect(cell).toBeInTheDocument();
    expect(cell).not.toHaveAttribute("data-tooltip-id");
  });

  it("mounts one tooltip regardless of how many cells are wrapped", () => {
    const observers: number[] = [];
    const RealMO = global.MutationObserver;
    class Counting extends RealMO {
      constructor(cb: MutationCallback) {
        super(cb);
        observers.push(1);
      }
    }
    (global as any).MutationObserver = Counting;

    try {
      render(
        <>
          {Array.from({ length: 20 }, (_, i) => (
            <AutodbTooltipWrapper key={i} info={{ ...ALPHA }}>
              {`cell ${i}`}
            </AutodbTooltipWrapper>
          ))}
          <AutodbTooltipHost />
        </>,
      );
      // One <Tooltip> instance's worth of observers, not one per wrapped cell.
      // The per-cell version mounted 40 per anchor; 20 cells would be ~800.
      expect(observers.length).toBeLessThan(20);
    } finally {
      (global as any).MutationObserver = RealMO;
    }
  });
});

describe("AutodbSourceButton", () => {
  it("opens the tooltip when tapped or clicked", async () => {
    render(
      <>
        <AutodbSourceButton info={ALPHA} />
        <AutodbTooltipHost />
      </>,
    );

    fireEvent.click(screen.getByRole("button", { name: "Source details" }));
    expect(await screen.findByText("Alpha comment")).toBeInTheDocument();
  });

  it("opens the tooltip on hover", async () => {
    render(
      <>
        <AutodbSourceButton info={ALPHA} />
        <AutodbTooltipHost />
      </>,
    );

    fireEvent.mouseOver(screen.getByRole("button", { name: "Source details" }));
    expect(await screen.findByText("Alpha comment")).toBeInTheDocument();
  });

  it("renders nothing when the tooltip would be empty", () => {
    render(
      <>
        <AutodbSourceButton info={{ source_id: "", metadata: {} }} />
        <AutodbSourceButton />
        <AutodbTooltipHost />
      </>,
    );

    expect(screen.queryByRole("button")).not.toBeInTheDocument();
  });
});
